'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { AccessPremiumEarnModal } from '@flipster/features/src/flipster-earn/premium-usdt/access-premium-earn-modal';
import { ManagePusdtModal } from '@flipster/features/src/flipster-earn/premium-usdt/manage-pusdt-modal';
import { earnSunsetPhaseBunja } from '@flipster/features/src/flipster-earn/sunset/phase-bunja';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import {
  earnSummaryBunja,
  getEarnSummaryOfCurrency,
  usdBalanceEmptyThreshold,
} from '@flipster/features/src/shared/earn/state';
import { useDepositModal } from '@flipster/features/src/shared/wallet-modal/deposit';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { useOverlay } from '@flipster/overlay';
import { walletsBunja } from '@flipster/r230522/original/wallet';
import { Button } from '@flipster/react/design-system/button';
import { hstack } from '@flipster/styled-system/patterns';
import { PusdtCurrency, UsdtCurrency } from '@flipster/utils/constants/currency';
import { isEarnSunsetProductCurrency } from '@flipster/utils/earn-sunset';
import Num from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { EarnEvent } from '@flipster/utils/segment/events/earn';

import { mintBunja } from '@web/app/[locale]/(web)/_modal/MintFlowModal/state';

import { EarnActionModal } from '../../earn-action-modal';

export const ActionButtons = ({ currency }: { currency: string }) => {
  const t = useTranslations();
  const overlay = useOverlay();
  const router = useRouter();
  const openDepositModal = useDepositModal();
  const { isLoggedInAtom } = useBunja(authBunja);
  const { isEarnSunsetBufferAtom } = useBunja(earnSunsetPhaseBunja);
  const { openMintModal } = useBunja(mintBunja);
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const { walletsAtom } = useBunja(walletsBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);
  const wallets = useAtomValue(walletsAtom);
  const usdtWalletBalance = Num(wallets?.[UsdtCurrency]?.walletBalanceExclWithdrawalPending ?? 0);
  const { isMintAvailable, isTradeAvailable } = getEarnSummaryOfCurrency(currency, earnSummary);

  // Open action modal when mint and trade is available
  const needToOpenActions = isMintAvailable || isTradeAvailable;
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isEarnSunsetBuffer = useAtomValue(isEarnSunsetBufferAtom);
  const isPusdt = currency === PusdtCurrency;
  const isSunsetReduceOnly = isEarnSunsetBuffer && isEarnSunsetProductCurrency(currency);

  const onClickSubscribe = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Subscribe', currency });
    overlay.open(({ isOpen, onClose, unmount }) => (
      <EarnActionModal isOpen={isOpen} onClose={onClose} currency={currency} onExit={unmount} />
    ));
  };

  const onClickRedeem = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Redeem', currency });
    openMintModal({ providerCurrency: currency, flowType: 'REDEEM' });
  };

  const handlePusdtAction = () => {
    if (usdtWalletBalance.lt(usdBalanceEmptyThreshold)) {
      overlay.open(({ isOpen, onClose, unmount }) => (
        <AccessPremiumEarnModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
      ));
      return;
    }
    overlay.open(({ isOpen, onClose, unmount }) => (
      <ManagePusdtModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
    ));
  };

  const onClickPusdtRedeem = () => {
    segment.track(EarnEvent.EarnManageClick, { button_text: 'Redeem', currency });
    overlay.open(({ isOpen, onClose, unmount }) => (
      <ManagePusdtModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
    ));
  };

  if (isSunsetReduceOnly) {
    return (
      <div className={hstack({ justify: 'center', gap: 8 })}>
        <Button
          label={t('Common.r251008.Redeem')}
          hierarchy='secondary'
          color='neutral'
          onClick={isPusdt ? onClickPusdtRedeem : onClickRedeem}
          css={{ w: { base: '50%', tablet: 160 } }}
        />
        <Button label={t('Autogen_common.r251126.s_Add')} disabled css={{ w: { base: '50%', tablet: 160 } }} />
      </div>
    );
  }

  if (needToOpenActions) {
    return (
      <div className={hstack({ justify: 'center', gap: 8 })}>
        {isMintAvailable && (
          <Button
            label={t('Common.r251008.Redeem')}
            hierarchy='secondary'
            color='neutral'
            onClick={onClickRedeem}
            css={{ w: { base: '50%', tablet: 160 } }}
          />
        )}
        <Button
          label={t('Autogen_common.r251126.s_Add')}
          onClick={onClickSubscribe}
          css={{ w: isMintAvailable ? { base: '50%', tablet: 160 } : { base: '100%', tablet: 296 } }}
        />
      </div>
    );
  }

  return (
    <div className={hstack({ justify: 'center' })}>
      <Button
        hierarchy='primary'
        color='accent'
        size='medium'
        label={
          isPusdt ? <FormattedMessage id='Common.r251112.Manage' /> : <FormattedMessage id='Common.r251107.Deposit' />
        }
        css={{ w: { base: '100%', tablet: 296 } }}
        onClick={() => {
          if (isLoggedIn) {
            segment.track(EarnEvent.EarnManageClick, { button_text: isPusdt ? 'Manage' : 'Deposit', currency });
            if (isPusdt) {
              handlePusdtAction();
            } else {
              openDepositModal({ defaultValues: { currency } });
            }
          } else {
            router.push('/login');
          }
        }}
      />
    </div>
  );
};
